/**
 * useMultiBookProgress Hook
 * Tracks reading progress across multiple books using IndexedDB
 */

import { useState, useEffect, useCallback } from 'react';
import {
  initDB,
  saveProgress,
  getProgress,
  getAllProgress,
  BookReadingProgress,
  ChapterProgress,
  ChapterInfo,
} from '../utils/indexedDB';
import { ContentSection } from '../types/content';

// ============================================================================
// Types
// ============================================================================

export interface OverallStats {
  booksStarted: number;
  totalWordsRead: number;
  chaptersCompleted: number;
  totalTimeMinutes: number;
}

export interface UseMultiBookProgressReturn {
  // State
  allProgress: BookReadingProgress[];
  isLoading: boolean;
  error: string | null;
  
  // Progress operations
  saveBookProgress: (
    bookId: string,
    bookTitle: string,
    chapterId: string,
    chapterTitle: string,
    wordIndex: number,
    wordsRead: number,
    metadata?: {
      totalChapters?: number;
      coverUrl?: string;
      categoryId?: string;
      totalWords?: number;
      author?: string;
      chapters?: ChapterInfo[];
      sections?: ContentSection[]; // for library stories to reload content
    }
  ) => Promise<boolean>;
  getProgressForBook: (bookId: string) => Promise<BookReadingProgress | null>;
  markChapterComplete: (bookId: string, chapterId: string) => Promise<void>;
  
  // Stats
  getOverallStats: () => OverallStats;
  
  // Refresh
  refreshProgress: () => Promise<void>;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useMultiBookProgress(): UseMultiBookProgressReturn {
  const [allProgress, setAllProgress] = useState<BookReadingProgress[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load all progress on mount
  useEffect(() => {
    loadProgress();
  }, []);

  /**
   * Load all progress from IndexedDB
   */
  const loadProgress = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      await initDB();
      const progress = await getAllProgress();
      setAllProgress(progress);
    } catch (err) {
      console.error('Failed to load progress:', err);
      setError(err instanceof Error ? err.message : 'Failed to load progress');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Save progress for a specific book
   */
  const saveBookProgress = useCallback(async (
    bookId: string,
    bookTitle: string,
    chapterId: string,
    chapterTitle: string,
    wordIndex: number,
    wordsRead: number,
    metadata?: {
      totalChapters?: number;
      coverUrl?: string;
      categoryId?: string;
      totalWords?: number;
      author?: string;
      chapters?: ChapterInfo[];
      sections?: ContentSection[]; // for library stories to reload content
    }
  ): Promise<boolean> => {
    let bookCompleted = false;
    try {
      // Get existing progress or create new
      let progress = await getProgress(bookId);
      const now = new Date().toISOString();
  
      if (!progress) {
        // Create new progress record
        progress = {
          bookId,
          bookTitle,
          currentChapterId: chapterId,
          currentChapterTitle: chapterTitle,
          currentWordIndex: wordIndex,
          totalWordsRead: wordsRead,
          lastReadAt: now,
          chapterProgress: [],
          // Include metadata if provided (for new records)
          ...(metadata?.totalChapters !== undefined && { totalChapters: metadata.totalChapters }),
          ...(metadata?.coverUrl && { coverUrl: metadata.coverUrl }),
          ...(metadata?.categoryId && { categoryId: metadata.categoryId }),
          ...(metadata?.totalWords !== undefined && { totalWords: metadata.totalWords }),
          ...(metadata?.author && { author: metadata.author }),
          ...(metadata?.chapters && { chapters: metadata.chapters }),
          ...(metadata?.sections && { sections: metadata.sections }),
        };
      } else {
        // Update existing progress
        progress.currentChapterId = chapterId;
        progress.currentChapterTitle = chapterTitle;
        progress.currentWordIndex = wordIndex;
        progress.totalWordsRead += wordsRead;
        progress.lastReadAt = now;
        // Note: metadata is only set on creation, not updated later
      }
  
      // At this point, progress is guaranteed to be non-null
      const p = progress!;

      // Update chapter progress
      let chapterProg = p.chapterProgress.find(cp => cp.chapterId === chapterId);
      if (!chapterProg) {
        chapterProg = {
          chapterId,
          chapterTitle,
          wordIndex: 0,
          completed: false,
          lastReadAt: now,
        };
        p.chapterProgress.push(chapterProg);
      }
      chapterProg.wordIndex = wordIndex;
      chapterProg.lastReadAt = now;

      // Detect book completion before saving
      const alreadyCompleted = p.completed || false;
      if (!alreadyCompleted) {
        if (p.totalWords && p.currentWordIndex >= p.totalWords) {
          p.completed = true;
          bookCompleted = true;
        } else if (p.chapters && p.chapters.length > 0 && p.chapterProgress.every(cp => cp.completed)) {
          p.completed = true;
          bookCompleted = true;
        }
      }

      // Save to IndexedDB
      await saveProgress(p);

      // Update local state
      setAllProgress(prev => {
        const existing = prev.findIndex(p => p.bookId === bookId);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = p;
          return updated;
        }
        return [p, ...prev];
      });

      return bookCompleted;
    } catch (err) {
      console.error('Failed to save progress:', err);
      throw err;
    }
  }, []);

  /**
   * Get progress for a specific book
   */
  const getProgressForBook = useCallback(async (bookId: string): Promise<BookReadingProgress | null> => {
    try {
      return await getProgress(bookId);
    } catch (err) {
      console.error('Failed to get progress for book:', err);
      return null;
    }
  }, []);

  /**
   * Mark a chapter as completed
   */
  const markChapterComplete = useCallback(async (bookId: string, chapterId: string): Promise<void> => {
    try {
      const progress = await getProgress(bookId);
      if (!progress) return;

      const chapterProg = progress.chapterProgress.find(cp => cp.chapterId === chapterId);
      if (chapterProg) {
        chapterProg.completed = true;
        chapterProg.lastReadAt = new Date().toISOString();

        // Check for book completion
        const alreadyCompleted = progress.completed || false;
        if (!alreadyCompleted) {
          if (progress.totalWords && progress.currentWordIndex >= progress.totalWords) {
            progress.completed = true;
          } else if (progress.chapters && progress.chapters.length > 0) {
            const allChaptersDone = progress.chapterProgress.every(cp => cp.completed);
            if (allChaptersDone) {
              progress.completed = true;
            }
          }
        }

        await saveProgress(progress);

        // Update local state
        setAllProgress(prev => {
          const existing = prev.findIndex(p => p.bookId === bookId);
          if (existing >= 0) {
            const updated = [...prev];
            updated[existing] = progress;
            return updated;
          }
          return prev;
        });
      }
    } catch (err) {
      console.error('Failed to mark chapter complete:', err);
    }
  }, []);

  /**
   * Calculate overall statistics
   */
  const getOverallStats = useCallback((): OverallStats => {
    let totalWordsRead = 0;
    let chaptersCompleted = 0;

    for (const progress of allProgress) {
      totalWordsRead += progress.totalWordsRead;
      chaptersCompleted += progress.chapterProgress.filter(cp => cp.completed).length;
    }

    // Estimate time (assuming 250 WPM average)
    const totalTimeMinutes = Math.ceil(totalWordsRead / 250);

    return {
      booksStarted: allProgress.length,
      totalWordsRead,
      chaptersCompleted,
      totalTimeMinutes,
    };
  }, [allProgress]);

  /**
   * Refresh progress from storage
   */
  const refreshProgress = useCallback(async (): Promise<void> => {
    await loadProgress();
  }, [loadProgress]);

  return {
    allProgress,
    isLoading,
    error,
    saveBookProgress,
    getProgressForBook,
    markChapterComplete,
    getOverallStats,
    refreshProgress,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Calculate progress percentage for a book
 */
export function calculateBookProgress(
  progress: BookReadingProgress,
  totalWords: number
): number {
  if (totalWords === 0) return 0;
  return Math.min(100, Math.round((progress.currentWordIndex / totalWords) * 100));
}

/**
 * Get the most recent books from progress
 */
export function getRecentBooks(
  allProgress: BookReadingProgress[],
  limit: number = 5
): BookReadingProgress[] {
  return [...allProgress]
    .sort((a, b) => new Date(b.lastReadAt).getTime() - new Date(a.lastReadAt).getTime())
    .slice(0, limit);
}

/**
 * Format progress for display
 */
export function formatProgress(current: number, total: number): string {
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
  return `${percentage}%`;
}

/**
 * Get chapter progress status
 */
export function getChapterStatus(
  progress: BookReadingProgress,
  chapterId: string
): 'not-started' | 'in-progress' | 'completed' {
  const chapterProg = progress.chapterProgress.find(cp => cp.chapterId === chapterId);
  
  if (!chapterProg) return 'not-started';
  if (chapterProg.completed) return 'completed';
  if (chapterProg.wordIndex > 0) return 'in-progress';
  return 'not-started';
}

/**
 * Estimate time remaining in a book
 */
export function estimateTimeRemaining(
  currentWordIndex: number,
  totalWords: number,
  wpm: number = 250
): number {
  const wordsRemaining = totalWords - currentWordIndex;
  return Math.ceil(wordsRemaining / wpm);
}

/**
 * Format time remaining for display
 */
export function formatTimeRemaining(minutes: number): string {
  if (minutes < 1) return '< 1 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}
